import React, { useState, useEffect } from 'react';
import { DollarSign, CreditCard, Clock, CheckCircle, XCircle, Eye, Search, ChevronLeft, ChevronRight, Menu, X, AlertTriangle, Globe, Mail, GamepadIcon } from 'lucide-react';
import { socket } from '../Containers/MainContainer';
import { Link, useParams } from 'react-router-dom';
import { numberWithCommas } from '../utils';

const PlutoWithdrawal = () => {
  const [withdrawals, setWithdrawals] = useState([]);
  const [stats, setStats] = useState({});
  const [pagination, setPagination] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('pending');
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const { referral_id, page2, second_page, third_page } = useParams();

  useEffect(() => {
    fetchWithdrawals();
  }, [currentPage, searchTerm, statusFilter]);

  const fetchWithdrawals = () => {
    setLoading(true);
    socket.emit('getWithdrawalsAdmin', {
      page: currentPage,
      search: searchTerm,
      status: statusFilter
    });

    socket.on('response_get_admin_withdrawals', (data) => {
      setWithdrawals(data.withdrawals || []);
      setStats({
        total_paid: data.stats.total_paid,
        total_requests: data.stats.total_requests,
        total_requests_cash: data.stats.total_requests_cash,
        processing_count:data.stats.processing_count,
        review_count:data.stats.review_count,
        urgent_pending:data.stats.urgent_pending,
        pending_count: data.stats.pending_count || 0,
        approved_count: data.stats.approved_count || 0,
        rejected_count: data.stats.rejected_count || 0
      });
      setPagination({
        currentPage: currentPage,
        totalPages: data.pagination.totalPages,
        totalWithdrawals: data.withdrawalsLength || 0,
        withdrawalsPerPage: 10
      });
      setLoading(false);
    });
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    setCurrentPage(1);
    setShowMobileFilters(false);
  };

  // Check for suspicious user activity
  const getSuspiciousFlags = (withdrawal) => {
    const flags = [];

    // Multiple countries check
    if ((withdrawal.flag_reason+'').includes('Multiple countries detected') || (withdrawal.flag_reason+'').includes('Country mismatch')) {
      flags.push({
        type: 'multiple_countries',
        icon: <Globe className="h-3 w-3" />,
        text: 'Multiple Countries',
        color: 'text-orange-400',
        tooltip: `Used multiple countries`
      });
    }

    // Temp email check
    const tempEmailDomains = ['tempmail', '10minutemail', 'guerrillamail', 'mailinator', 'yopmail', 'throwaway'];
    if ((withdrawal.flag_reason+'').includes('email pattern')) {
      flags.push({
        type: 'temp_email',
        icon: <Mail className="h-3 w-3" />,
        text: 'Temp Email',
        color: 'text-red-400',
        tooltip: `Temporary email: ${withdrawal.email}`
      });
    }

    // US playtime check (if user is not from US but has US playtime)
    if ((withdrawal.flag_reason+'').toLowerCase().includes('play time')) {
      flags.push({
        type: 'us_playtime',
        icon: <GamepadIcon className="h-3 w-3" />,
        text: 'US Play',
        color: 'text-yellow-400',
        tooltip: `${withdrawal.us_playtime} minutes US playtime from ${withdrawal.user_country}`
      });
    }

    return flags;
  };

  const getStatusBadge = (withdrawal) => {
    const { status, verified_auto_pay, status_text } = withdrawal;

    const statusMap = {
      'paid': { color: 'green', text: 'Paid', icon: '\u2713' },
      'pending': { color: 'yellow', text: 'Pending Review', icon: '\u23F3' },
      'processing': {
        color: verified_auto_pay ? 'blue' : 'purple',
        text: verified_auto_pay ? 'Auto Processing' : 'Manual Processing',
        icon: verified_auto_pay ? '\uD83E\uDD16' : '\uD83D\uDC64'
      },
      'review': { color: 'orange', text: 'In Review', icon: '\uD83D\uDC41\uFE0F' }
    };

    const statusInfo = statusMap[status] || { color: 'gray', text: status_text || 'Unknown', icon: '?' };

    const badgeStyles = {
      green: { background: 'rgba(63,185,80,0.15)', color: '#3fb950' },
      yellow: { background: 'rgba(210,153,34,0.15)', color: '#d29922' },
      blue: { background: 'rgba(88,166,255,0.15)', color: '#58a6ff' },
      purple: { background: 'rgba(168,85,247,0.15)', color: '#a855f7' },
      orange: { background: 'rgba(249,115,22,0.15)', color: '#f97316' },
      gray: { background: 'rgba(139,148,158,0.15)', color: '#8b949e' }
    };

    const style = badgeStyles[statusInfo.color] || badgeStyles.gray;

    return (
      <span style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '2px 8px',
        borderRadius: '9999px',
        fontSize: '12px',
        fontWeight: 500,
        background: style.background,
        color: style.color
      }}>
        <span style={{ marginRight: '4px' }}>{statusInfo.icon}</span>
        <span className="hidden sm:inline">{statusInfo.text}</span>
        <span className="sm:hidden">{statusInfo.icon}</span>
      </span>
    );
  };

  const StatCard = ({ icon: Icon, title, value, subtitle, color = "blue" }) => {
    const colorMap = {
      blue: '#58a6ff',
      green: '#3fb950',
      yellow: '#d29922',
      purple: '#a855f7',
      orange: '#f97316',
      red: '#f85149'
    };
    const iconColor = colorMap[color] || colorMap.blue;

    return (
      <div style={{
        background: '#161b22',
        border: '1px solid #21262d',
        borderRadius: '12px',
        padding: '8px 12px',
        minWidth: '120px',
        flexShrink: 0
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ padding: '4px', borderRadius: '6px' }}>
            <Icon style={{ height: '16px', width: '16px', color: iconColor }} />
          </div>
          <div>
            <div style={{ fontSize: '14px', fontWeight: 700, color: '#e6edf3' }}>
              {typeof value === 'number' ? numberWithCommas(value) : value || '0'}
            </div>
            <div style={{ fontSize: '12px', color: '#8b949e' }}>{title}</div>
          </div>
        </div>
      </div>
    );
  };

  const PaginationButton = ({ page, active, onClick, disabled, children }) => (
    <button
      onClick={() => onClick(page)}
      disabled={disabled}
      style={{
        padding: '6px 8px',
        fontSize: '14px',
        borderRadius: '6px',
        transition: 'all 0.2s',
        border: 'none',
        cursor: disabled ? 'not-allowed' : 'pointer',
        background: active ? '#238636' : '#161b22',
        color: active ? '#ffffff' : disabled ? '#484f58' : '#c9d1d9',
        opacity: disabled ? 0.6 : 1
      }}
    >
      {children || page}
    </button>
  );

  const MobileWithdrawalCard = ({ withdrawal }) => {
    const suspiciousFlags = getSuspiciousFlags(withdrawal);

    return (
      <div style={{
        background: '#161b22',
        borderRadius: '12px',
        padding: '12px',
        border: '1px solid #21262d'
      }}>
        {/* Header with ID and Status */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
          <div>
            <div style={{ fontSize: '14px', fontWeight: 500, color: '#c9d1d9' }}>#{withdrawal.id}</div>
            <div style={{ fontSize: '12px', color: '#484f58' }}>
              ({withdrawal.user_id}) {withdrawal.username || 'Unknown'}
            </div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
            {getStatusBadge(withdrawal)}
            {withdrawal.verified_auto_pay && (
              <div style={{ fontSize: '12px', color: '#58a6ff' }}>{'\uD83E\uDD16'}</div>
            )}
          </div>
        </div>

        {/* Suspicious Activity Warnings */}
        {suspiciousFlags.length > 0 && (
          <div style={{
            marginBottom: '8px',
            padding: '8px',
            background: 'rgba(248,81,73,0.1)',
            border: '1px solid rgba(248,81,73,0.2)',
            borderRadius: '8px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', marginBottom: '4px' }}>
              <AlertTriangle style={{ height: '12px', width: '12px', color: '#f85149' }} />
              <span style={{ fontSize: '12px', color: '#f85149', fontWeight: 500 }}>Suspicious Activity</span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
              {suspiciousFlags.map((flag, index) => (
                <div
                  key={index}
                  className={flag.color}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '2px 6px',
                    background: 'rgba(22,27,34,0.5)',
                    borderRadius: '4px',
                    fontSize: '12px'
                  }}
                  title={flag.tooltip}
                >
                  {flag.icon}
                  <span>{flag.text}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Compact Info Grid */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '8px', fontSize: '12px' }}>
          <div>
            <span style={{ color: '#8b949e' }}>Amount:</span>
            <span style={{ color: '#e6edf3', fontWeight: 500, marginLeft: '4px' }}>{withdrawal.amount || '0'}</span>
          </div>
          <div>
            <span style={{ color: '#8b949e' }}>Type:</span>
            <span style={{ color: '#e6edf3', marginLeft: '4px' }}>{withdrawal.type || 'N/A'}</span>
          </div>
          <div>
            <span style={{ color: '#8b949e' }}>Date:</span>
            <span style={{ color: '#e6edf3', marginLeft: '4px' }}>{withdrawal.created_date}</span>
          </div>
          <div>
            <span style={{ color: '#8b949e' }}>Method:</span>
            <span style={{ color: '#e6edf3', marginLeft: '4px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{withdrawal.method || 'N/A'}</span>
          </div>
        </div>

        {/* Days Pending Warning */}
        {withdrawal.days_pending !== null && withdrawal.days_pending > 0 && (
          <div style={{
            fontSize: '12px',
            marginBottom: '8px',
            color: withdrawal.days_pending > 2 ? '#f85149' : '#d29922'
          }}>
            {'\u23F0'} {withdrawal.days_pending} days pending
          </div>
        )}

        {/* Flag Reason */}
        {withdrawal.flag_reason && (
          <div style={{ fontSize: '12px', color: '#f97316', marginBottom: '8px' }} title={withdrawal.flag_reason}>
            {'\u26A0\uFE0F'} {withdrawal.flag_reason.length > 30 ? withdrawal.flag_reason.substring(0, 30) + '...' : withdrawal.flag_reason}
          </div>
        )}

        {/* Action Button */}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Link
            to={`/pluto/withdrawals/${withdrawal.id}`}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              color: '#58a6ff',
              textDecoration: 'none',
              fontSize: '12px',
              fontWeight: 500,
              transition: 'color 0.2s'
            }}
          >
            <Eye style={{ height: '12px', width: '12px' }} />
            View
          </Link>
        </div>
      </div>
    );
  };

  return (
    <div style={{ minHeight: '100vh', background: '#0d1117', color: '#e6edf3' }}>
      {/* Mobile Header - Fixed and Compact */}
      <div className="lg:hidden" style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        background: '#0d1117',
        borderBottom: '1px solid #21262d',
        zIndex: 40
      }}>
        <div style={{ padding: '8px 12px' }}>
          <h1 style={{ fontSize: '18px', fontWeight: 700, color: '#e6edf3', margin: 0 }}>Withdrawals</h1>
        </div>
      </div>

      {/* Mobile Content Spacer */}

      <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '8px' }} className="lg:p-6">
        {/* Desktop Header */}
        <div className="hidden lg:block" style={{ marginBottom: '24px' }}>
          <h1 style={{ fontSize: '30px', fontWeight: 700, color: '#e6edf3', margin: 0 }}>Withdrawal Management</h1>
          <p style={{ marginTop: '8px', color: '#c9d1d9' }}>Monitor and manage withdrawal requests</p>
        </div>

        {/* Compact Stats Grid */}
        <div style={{ display: 'flex', overflowX: 'auto', gap: '8px', marginBottom: '12px', paddingBottom: '4px' }}>
          <StatCard icon={DollarSign} title="Paid" value={`$${stats.total_paid || '0'}`} color="green" />
          <StatCard icon={CreditCard} title="Requests" value={`$${stats.total_requests_cash || '0'}`} color="blue" />
          <StatCard icon={Clock} title="Pending" value={stats.pending_count || 0} color="yellow" />
          <StatCard icon={Eye} title="Processing" value={stats.processing_count || 0} color="purple" />
          <StatCard icon={CheckCircle} title="Review" value={stats.review_count || 0} color="orange" />
          <StatCard icon={XCircle} title="Urgent" value={stats.urgent_pending || 0} color="red" />
        </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
            <button
              onClick={ ()=>{
                socket.emit('adminAction', {
                    action: 'recheck_withdrawals',
                  });
                socket.emit('getWithdrawalsAdmin', {
      page: currentPage,
      search: searchTerm,
      status: statusFilter
    });}}
              style={{
                padding: '8px 12px',
                borderRadius: '8px',
                fontSize: '14px',
                transition: 'all 0.2s',
                background: '#161b22',
                color: '#c9d1d9',
                border: '1px solid #21262d',
                cursor: 'pointer'
              }}
            >
              <span>Recheck All</span>
            </button>
             <button
              onClick={()=>{socket.emit('adminAction', {
                    action: 'release_withdrawals',
                  });
                socket.emit('getWithdrawalsAdmin', {
      page: currentPage,
      search: searchTerm,
      status: statusFilter
    });}}
              style={{
                padding: '8px 12px',
                borderRadius: '8px',
                fontSize: '14px',
                transition: 'all 0.2s',
                background: '#161b22',
                color: '#c9d1d9',
                border: '1px solid #21262d',
                cursor: 'pointer'
              }}
            >
              <span>Release All</span>
            </button>
            </div>
        {/* Compact Mobile Filters */}
        <div className="lg:hidden" style={{ marginBottom: '12px' }}>
          <button
            onClick={() => setShowMobileFilters(!showMobileFilters)}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '8px 12px',
              background: '#161b22',
              borderRadius: '8px',
              fontSize: '14px',
              color: '#c9d1d9',
              border: '1px solid #21262d',
              cursor: 'pointer'
            }}
          >
            <span>
              {statusFilter === 'pending' ? '\u23F3 Pending' :
               statusFilter === 'all' ? 'All' :
               statusFilter === 'processing' ? '\u26A1 Processing' :
               statusFilter === 'review' ? '\uD83D\uDC41\uFE0F Review' :
               statusFilter === 'paid' ? '\u2705 Paid' :
               statusFilter === 'auto_verify' ? '\uD83E\uDD16 Auto' : 'Filter'}
            </span>
            {showMobileFilters ? <X style={{ height: '16px', width: '16px' }} /> : <Menu style={{ height: '16px', width: '16px' }} />}
          </button>
        </div>

        {/* Filter Buttons */}
        <div className={`${showMobileFilters ? 'grid' : 'hidden'} lg:flex`} style={{ gridTemplateColumns: '1fr 1fr', gap: '8px', marginBottom: '12px' }}>
          {[
            { key: 'pending', label: '\u23F3 Pending', icon: '\u23F3' },
            { key: 'all', label: 'All', icon: '\uD83D\uDCCB' },
            { key: 'processing', label: '\u26A1 Processing', icon: '\u26A1' },
            { key: 'review', label: '\uD83D\uDC41\uFE0F Review', icon: '\uD83D\uDC41\uFE0F' },
            { key: 'paid', label: '\u2705 Paid', icon: '\u2705' },
            { key: 'auto_verify', label: '\uD83E\uDD16 Auto', icon: '\uD83E\uDD16' }
          ].map(filter => (
            <button
              key={filter.key}
              onClick={() => handleStatusFilter(filter.key)}
              style={{
                padding: '8px 12px',
                borderRadius: '8px',
                fontSize: '14px',
                transition: 'all 0.2s',
                cursor: 'pointer',
                border: statusFilter === filter.key ? '1px solid #2ea043' : '1px solid #21262d',
                background: statusFilter === filter.key ? '#238636' : '#161b22',
                color: statusFilter === filter.key ? '#ffffff' : '#c9d1d9'
              }}
            >
              <span className="lg:hidden">{filter.icon}</span>
              <span className="hidden lg:inline">{filter.label}</span>
            </button>
          ))}
        </div>

        {/* Main Content */}
        <div style={{
          background: '#161b22',
          borderRadius: '12px',
          border: '1px solid #21262d'
        }}>
          {/* Compact Search */}
          <div style={{ padding: '12px', borderBottom: '1px solid #21262d' }}>
            <div style={{ position: 'relative' }}>
              <Search style={{
                position: 'absolute',
                left: '8px',
                top: '50%',
                transform: 'translateY(-50%)',
                height: '16px',
                width: '16px',
                color: '#8b949e'
              }} />
              <input
                type="text"
                placeholder="Search by ID or amount..."
                value={searchTerm}
                onChange={handleSearch}
                style={{
                  paddingLeft: '32px',
                  paddingRight: '12px',
                  paddingTop: '8px',
                  paddingBottom: '8px',
                  width: '100%',
                  background: '#0d1117',
                  color: '#e6edf3',
                  borderRadius: '8px',
                  fontSize: '14px',
                  border: '1px solid #30363d',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
                onFocus={(e) => e.target.style.borderColor = '#58a6ff'}
                onBlur={(e) => e.target.style.borderColor = '#30363d'}
              />
            </div>
          </div>

          {/* Desktop Table */}
          <div className="hidden lg:block" style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#161b22', borderBottom: '1px solid #21262d' }}>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '11px', fontWeight: 500, color: '#8b949e', textTransform: 'uppercase', letterSpacing: '0.8px' }}>ID</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '11px', fontWeight: 500, color: '#8b949e', textTransform: 'uppercase', letterSpacing: '0.8px' }}>User</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '11px', fontWeight: 500, color: '#8b949e', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Type</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '11px', fontWeight: 500, color: '#8b949e', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Amount</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '11px', fontWeight: 500, color: '#8b949e', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Date</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '11px', fontWeight: 500, color: '#8b949e', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Status</th>
                  <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '11px', fontWeight: 500, color: '#8b949e', textTransform: 'uppercase', letterSpacing: '0.8px' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  Array.from({ length: 10 }).map((_, i) => (
                    <tr key={i} className="animate-pulse" style={{ borderBottom: '1px solid #21262d' }}>
                      <td style={{ padding: '12px 16px' }}><div style={{ height: '16px', background: '#21262d', borderRadius: '4px', width: '48px' }}></div></td>
                      <td style={{ padding: '12px 16px' }}><div style={{ height: '16px', background: '#21262d', borderRadius: '4px', width: '96px' }}></div></td>
                      <td style={{ padding: '12px 16px' }}><div style={{ height: '16px', background: '#21262d', borderRadius: '4px', width: '64px' }}></div></td>
                      <td style={{ padding: '12px 16px' }}><div style={{ height: '16px', background: '#21262d', borderRadius: '4px', width: '80px' }}></div></td>
                      <td style={{ padding: '12px 16px' }}><div style={{ height: '16px', background: '#21262d', borderRadius: '4px', width: '96px' }}></div></td>
                      <td style={{ padding: '12px 16px' }}><div style={{ height: '24px', background: '#21262d', borderRadius: '4px', width: '64px' }}></div></td>
                      <td style={{ padding: '12px 16px' }}><div style={{ height: '24px', background: '#21262d', borderRadius: '4px', width: '48px' }}></div></td>
                    </tr>
                  ))
                ) : (
                  withdrawals.map((withdrawal) => {
                    const suspiciousFlags = getSuspiciousFlags(withdrawal);
                    return (
                      <tr key={withdrawal.id} style={{ borderBottom: '1px solid #21262d', transition: 'background 0.2s' }}
                        onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(33,38,45,0.5)'}
                        onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                      >
                        <td style={{ padding: '12px 16px', fontSize: '14px', color: '#c9d1d9' }}>#{withdrawal.id}</td>
                        <td style={{ padding: '12px 16px' }}>
                          <div>
                            <div style={{ fontSize: '14px', color: '#c9d1d9' }}>
                              ({withdrawal.user_id}) {withdrawal.username || 'Unknown'}
                            </div>
                            {suspiciousFlags.length > 0 && (
                              <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
                                {suspiciousFlags.map((flag, index) => (
                                  <div key={index} className={flag.color} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px' }} title={flag.tooltip}>
                                    {flag.icon}
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: '14px', color: '#c9d1d9' }}>{withdrawal.type}</td>
                        <td style={{ padding: '12px 16px', fontSize: '14px', color: '#c9d1d9' }}>{withdrawal.amount}</td>
                        <td style={{ padding: '12px 16px', fontSize: '14px', color: '#c9d1d9' }}>{withdrawal.created_date}</td>
                        <td style={{ padding: '12px 16px' }}>{getStatusBadge(withdrawal)}
                            {withdrawal.flag_reason && (
          <div style={{ fontSize: '12px', color: '#f97316', marginBottom: '8px' }} title={withdrawal.flag_reason}>
            {'\u26A0\uFE0F'} {withdrawal.flag_reason.length > 30 ? withdrawal.flag_reason.substring(0, 30) + '...' : withdrawal.flag_reason}
          </div>
        )}
                        </td>
                        <td style={{ padding: '12px 16px' }}>
                          <Link to={`/pluto/withdrawals/${withdrawal.id}`} style={{ color: '#58a6ff', transition: 'color 0.2s' }}>
                            <Eye style={{ height: '16px', width: '16px' }} />
                          </Link>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="lg:hidden">
            {loading ? (
              <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="animate-pulse" style={{
                    background: '#21262d',
                    borderRadius: '12px',
                    padding: '12px'
                  }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div style={{ height: '16px', background: '#30363d', borderRadius: '4px', width: '64px' }}></div>
                        <div style={{ height: '20px', background: '#30363d', borderRadius: '4px', width: '48px' }}></div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
                        <div style={{ height: '12px', background: '#30363d', borderRadius: '4px' }}></div>
                        <div style={{ height: '12px', background: '#30363d', borderRadius: '4px' }}></div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {withdrawals.map((withdrawal) => (
                  <MobileWithdrawalCard key={withdrawal.id} withdrawal={withdrawal} />
                ))}
                {withdrawals.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '32px 0', color: '#484f58' }}>No withdrawals found</div>
                )}
              </div>
            )}
          </div>

          {/* Compact Pagination */}
          {pagination.totalPages > 1 && (
            <div style={{ borderTop: '1px solid #21262d', padding: '12px', marginRight: '40px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '14px', color: '#8b949e' }}>
                <span>
                  {((currentPage - 1) * pagination.withdrawalsPerPage) + 1}-{Math.min(currentPage * pagination.withdrawalsPerPage, pagination.totalWithdrawals)} of {numberWithCommas(pagination.totalWithdrawals)}
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <PaginationButton
                    page={currentPage - 1}
                    onClick={handlePageChange}
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft style={{ height: '16px', width: '16px' }} />
                  </PaginationButton>
                  <span style={{ padding: '0 8px', color: '#c9d1d9' }}>{currentPage} / {numberWithCommas(pagination.totalPages)}</span>
                  <PaginationButton
                    page={currentPage + 1}
                    onClick={handlePageChange}
                    disabled={currentPage === pagination.totalPages}
                  >
                    <ChevronRight style={{ height: '16px', width: '16px' }} />
                  </PaginationButton>
                </div>
              </div>
            </div>
          )}
          {/* Quick jump input */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 12px' }}>
          <span style={{ fontSize: '14px', color: '#8b949e' }}>Go to:</span>
          <input
            type="number"
            min="1"
            max={pagination.totalPages}
            placeholder={currentPage}
            style={{
              width: '80px',
              padding: '4px 8px',
              fontSize: '14px',
              background: '#0d1117',
              color: '#e6edf3',
              border: '1px solid #30363d',
              borderRadius: '6px',
              outline: 'none'
            }}
            onFocus={(e) => e.target.style.borderColor = '#58a6ff'}
            onBlur={(e) => e.target.style.borderColor = '#30363d'}
            onKeyPress={(e) => {
              if (e.key === 'Enter') {
                const page = parseInt(e.target.value);
                if (page >= 1 && page <= pagination.totalPages) {
                  handlePageChange(page);
                  e.target.value = '';
                }
              }
            }}
          />
          <span style={{ fontSize: '14px', color: '#8b949e' }}>of {numberWithCommas(pagination.totalPages)}</span>
        </div>
        </div>

      </div>
    </div>
  );
};

export default PlutoWithdrawal;
